import { useTranslation } from "react-i18next";
import { Send, ShieldCheck, MousePointerClick, FileCheck, Plane, ArrowRight } from "lucide-react";

const HowItWorksSection = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: Send,
      number: 1,
      titleKey: "howItWorks.steps.request.title",
      descriptionKey: "howItWorks.steps.request.description"
    },
    {
      icon: ShieldCheck,
      number: 2,
      titleKey: "howItWorks.steps.assess.title",
      descriptionKey: "howItWorks.steps.assess.description"
    },
    {
      icon: MousePointerClick,
      number: 3,
      titleKey: "howItWorks.steps.approve.title",
      descriptionKey: "howItWorks.steps.approve.description"
    },
    {
      icon: FileCheck,
      number: 4,
      titleKey: "howItWorks.steps.handle.title",
      descriptionKey: "howItWorks.steps.handle.description"
    },
    {
      icon: Plane,
      number: 5,
      titleKey: "howItWorks.steps.travel.title",
      descriptionKey: "howItWorks.steps.travel.description"
    }
  ];

  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            {t('howItWorks.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('howItWorks.subtitle')}
          </p>
        </div>

        {/* Desktop: Horizontal timeline */}
        <div className="hidden lg:block max-w-6xl mx-auto">
          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/20 via-primary to-primary/20"></div>
            
            <div className="grid grid-cols-5 gap-4">
              {steps.map((step, index) => (
                <div key={index} className="relative flex flex-col items-center text-center">
                  {/* Step circle */}
                  <div className="relative z-10 w-24 h-24 bg-white border-4 border-primary rounded-full flex items-center justify-center shadow-lg mb-6">
                    <step.icon className="h-10 w-10 text-primary" />
                  </div>
                  
                  {/* Number badge */}
                  <div className="absolute top-0 right-1/2 transform translate-x-12 -translate-y-1 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                    {step.number}
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-2 text-foreground">
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t(step.descriptionKey)}
                  </p>
                  
                  {/* Arrow between steps */}
                  {index < steps.length - 1 && (
                    <div className="absolute top-12 -right-2 transform translate-x-1/2 z-20">
                      <ArrowRight className="h-5 w-5 text-primary" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: Vertical stack */}
        <div className="lg:hidden max-w-md mx-auto">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-primary to-primary/20"></div>
            
            <div className="space-y-8">
              {steps.map((step, index) => (
                <div key={index} className="relative flex gap-6">
                  {/* Step circle */}
                  <div className="relative z-10 flex-shrink-0 w-12 h-12 bg-white border-4 border-primary rounded-full flex items-center justify-center shadow-lg">
                    <step.icon className="h-5 w-5 text-primary" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-5 h-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                        {step.number}
                      </span>
                      <h3 className="font-semibold text-lg text-foreground">
                        {t(step.titleKey)}
                      </h3>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {t(step.descriptionKey)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
